// UI Components
import LineItemDisplay from '@/components/lines/LineItemDisplay'
import ShopifyVariantSelect from '@/components/ui/ShopifyVariantSelect'
import ComponentSelect2 from '@/components/ui/ComponentSelect2'
import UiBox from '@/components/ui/Box'

/**
 * Reusable component for editing line items (products/components)
 *
 * @param {Object} props
 * @param {Array} props.lines - Array of line items from useLineItems hook
 * @param {Function} props.addComponent - Function to add a component
 * @param {Function} props.addVariant - Function to add a Shopify variant
 * @param {Function} props.updateLinePrice - Function to update line price
 * @param {Function} props.updateLineQuantity - Function to update line quantity
 * @param {Function} props.removeLine - Function to remove a line
 * @param {number} props.total_without_shipping - Total price without shipping
 * @param {number} props.total_quantity - Total quantity of items
 * @param {string} [props.title] - Optional title for the box
 * @param {Function} [props.isLineAvailable] - Optional function to check if a line has availability issues (returns true if available)
 */
export default function LineItemsEditor({
    lines,
    addComponent,
    addVariant,
    updateLinePrice,
    updateLineQuantity,
    removeLine,
    total_without_shipping,
    total_quantity,
    title = 'Produits à préparer',
    isLineAvailable,
    children,
}) {
    return (
        <UiBox title={title}>
            <div className="flex flex-col gap-5">
                {/* Display existing lines */}
                {lines.length > 0 && (
                    <div className="flex flex-col gap-5 border rounded-xl p-5 divide-y divide-gray-200">
                        {lines.map((line, index) => (
                            <LineItemDisplay
                                key={`${line.item_type}-${
                                    line.component_id ||
                                    line.shopify_product_variant_id
                                }`}
                                line={line}
                                onPriceChange={(price) =>
                                    updateLinePrice(index, price)
                                }
                                onQuantityChange={(quantity) =>
                                    updateLineQuantity(index, quantity)
                                }
                                onRemove={() => removeLine(index)}
                                hasAvailabilityIssue={
                                    isLineAvailable
                                        ? !isLineAvailable(line)
                                        : false
                                }
                            />
                        ))}
                    </div>
                )}

                {lines.length === 0 && (
                    <div className="border flex justify-center p-5 bg-gray-50 text-gray-400 text-sm text-center rounded-xl">
                        Aucune ligne
                    </div>
                )}

                {children}

                {/* Add items */}
                <div className="grid grid-cols-2 gap-3">
                    <ShopifyVariantSelect
                        label="Ajouter une variante"
                        onSelect={(variant) => addVariant(variant.id)}
                    />
                    <ComponentSelect2
                        label="Ajouter un composant"
                        onSelect={(component) => addComponent(component.id)}
                    />
                </div>
            </div>

            {/* Summary */}
            {lines.length > 0 && (
                <div className="mt-5 p-4 bg-gray-50 rounded-lg flex justify-between">
                    <span className="font-medium">Valeur douanière</span>
                    <div className="flex gap-5">
                        <span>{total_quantity} article(s)</span>
                        <span className="font-medium">
                            {new Intl.NumberFormat('fr-FR', {
                                style: 'currency',
                                currency: 'EUR',
                            }).format(total_without_shipping)}
                        </span>
                    </div>
                </div>
            )}
        </UiBox>
    )
}
